import { ChangeDetectionStrategy, Component, computed, inject, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { HmiLookupService } from '../../../../services/hmi-lookup.service';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

@Component({
  selector: 'kalite-edit',
  standalone: true,
  imports: [FormsModule, NzInputModule, NzInputNumberModule, NzSelectModule,
    CdkTextareaAutosize],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ke-edit">
      <div class="ke-baslik">
        <div class="ke-baslik-ikon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
        </div>
        <span>Kalite Kontrol Bilgileri</span>
      </div>

      <div class="ke-form">
        <div class="form-grup">
          <label>Ürün Adı <span class="zorunlu">*</span></label>
          <div class="input-wrap">
            <input nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.urunAdi || ''"
              (ngModelChange)="guncelle('urunAdi', $event)"
              placeholder="Ürün adını girin..."/>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Part No</label>
          <div class="input-wrap">
            <input nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.partNo || ''"
              (ngModelChange)="guncelle('partNo', $event)"
              placeholder="Part numarası"/>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="ke-ayirici"></div>

        <div class="form-satir">
          <div class="form-grup f1">
            <label>Ölçü Değer</label>
            <div class="input-wrap">
              <nz-input-number
                [ngModel]="p()?.olcuDeger"
                (ngModelChange)="guncelle('olcuDeger', $event)"
                [nzStep]="0.01" style="width:100%">
              </nz-input-number>
              <div class="input-glow"></div>
            </div>
          </div>
          <div class="form-grup f1">
            <label>Tolerans Alt</label>
            <div class="input-wrap">
              <nz-input-number
                [ngModel]="p()?.toleransAlt"
                (ngModelChange)="guncelle('toleransAlt', $event)"
                [nzStep]="0.01" style="width:100%">
              </nz-input-number>
              <div class="input-glow"></div>
            </div>
          </div>
          <div class="form-grup f1">
            <label>Tolerans Üst</label>
            <div class="input-wrap">
              <nz-input-number
                [ngModel]="p()?.toleransUst"
                (ngModelChange)="guncelle('toleransUst', $event)"
                [nzStep]="0.01" style="width:100%">
              </nz-input-number>
              <div class="input-glow"></div>
            </div>
          </div>
        </div>

        <div class="ke-ayirici"></div>

        <div class="form-grup">
          <label>Sonuç</label>
          <div class="sonuc-secici">
            @for (s of sonuclar; track s.deger) {
              <button class="sonuc-btn"
                [class.secili]="p()?.sonuc === s.deger"
                [style.--_s]="s.renk"
                (click)="guncelle('sonuc', s.deger)">
                <span class="sonuc-dot"></span>
                {{ s.deger }}
              </button>
            }
          </div>
        </div>

        <div class="form-grup">
          <label>Kontrol Eden</label>
          <div class="input-wrap">
            <nz-select
              [ngModel]="p()?.kontrolEden"
              (ngModelChange)="guncelle('kontrolEden', $event)"
              nzPlaceHolder="Kontrol eden seçin..."
              nzAllowClear nzShowSearch
              style="width:100%"
              [nzCustomTemplate]="kontrolEdenTpl">
              @for (kisi of lookup.personeller(); track kisi._id) {
                <nz-option [nzValue]="kisi.ad + ' ' + kisi.soyad" [nzLabel]="kisi.ad + ' ' + kisi.soyad" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">👤</span><div class="sel-bilgi"><span class="sel-ad">{{ kisi.ad }} {{ kisi.soyad }}</span><span class="sel-detay">{{ kisi.unvan || kisi.gorev || '' }}{{ kisi.sicilNo ? ' · ' + kisi.sicilNo : '' }}</span></div></div>
                </nz-option>
              }
            </nz-select>
            <ng-template #kontrolEdenTpl let-selected>
              <span class="sel-item"><span class="sel-emoji">👤</span><span class="sel-ad">{{ selected.nzLabel }}</span></span>
            </ng-template>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Notlar</label>
          <div class="input-wrap">
            <textarea nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.notlar || ''"
              (ngModelChange)="guncelle('notlar', $event)"
              placeholder="Kontrol notları..."
              cdkTextareaAutosize [cdkAutosizeMinRows]="2" [cdkAutosizeMaxRows]="4">
            </textarea>
            <div class="input-glow"></div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`

    :host { display: block; }

    .ke-edit { display: flex; flex-direction: column; gap: 0; padding: 0; }

    .ke-baslik {
      display: flex; align-items: center; gap: 10px;
      padding: 12px 16px; margin-bottom: 4px; border-radius: 10px;
      background: linear-gradient(135deg, var(--py-arkaplan-soluk, rgba(0,0,0,0.03)), transparent);
    }

    .ke-baslik-ikon {
      width: 32px; height: 32px; border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      background: linear-gradient(135deg, #166534, #22c55e);
      color: #fff; flex-shrink: 0;
      box-shadow: 0 2px 8px rgba(34, 197, 94, 0.35);
      position: relative;
    }

    .ke-baslik-ikon::after {
      content: ''; position: absolute; inset: 0; border-radius: inherit;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
      background-size: 200% 100%;
      animation: shimmer 3s infinite linear;
      pointer-events: none;
    }

    .ke-baslik span { font-size: 13px; font-weight: 600; color: var(--py-metin, #333); letter-spacing: 0.3px; }

    .ke-form { display: flex; flex-direction: column; gap: 14px; padding: 4px 2px; }

    .ke-ayirici {
      height: 1px;
      background: linear-gradient(90deg, transparent, var(--py-kenarlik, #d9d9d9) 20%, var(--py-kenarlik, #d9d9d9) 80%, transparent);
      opacity: 0.4; margin: 2px 0;
    }

    .form-satir { display: flex; gap: 12px; }
    .f1 { flex: 1; }

    .form-grup { display: flex; flex-direction: column; gap: 5px; }

    .form-grup label {
      font-size: 11px; font-weight: 500;
      color: var(--py-metin-ucuncul, rgba(0,0,0,0.45));
      text-transform: uppercase; letter-spacing: 0.6px;
      padding-left: 2px; transition: color 0.2s ease;
    }
    .form-grup:focus-within label { color: var(--py-link, #1890ff); }

    .zorunlu { color: #ff4d4f; font-size: 13px; line-height: 1; vertical-align: middle; }

    .input-wrap { position: relative; }
    .input-glow {
      position: absolute; bottom: 0; left: 50%; width: 0; height: 2px; border-radius: 1px;
      background: linear-gradient(90deg, #166534, #22c55e, #166534);
      transition: width 0.35s cubic-bezier(0.4,0,0.2,1), left 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease;
      opacity: 0; pointer-events: none; z-index: 1;
    }
    .input-wrap:focus-within .input-glow { width: 100%; left: 0; opacity: 1; }

    .input-wrap input[nz-input],
    .input-wrap textarea[nz-input] {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02));
      border: 1px solid var(--py-kenarlik, #d9d9d9);
      border-radius: 8px; padding: 7px 11px; font-size: 13px;
      color: var(--py-metin, #333);
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }
    .input-wrap input[nz-input]:hover,
    .input-wrap textarea[nz-input]:hover { border-color: var(--py-kenarlik-hover, #b3b3b3); background: var(--py-arkaplan-hover, #f5f5f5); }
    .input-wrap input[nz-input]:focus,
    .input-wrap textarea[nz-input]:focus { border-color: var(--py-kenarlik-aktif, #1890ff); background: var(--py-input-arka, #fff); box-shadow: 0 0 0 2px rgba(24,144,255,0.1); outline: none; }

    .input-wrap ::ng-deep .ant-select-selector {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02)) !important;
      border: 1px solid var(--py-kenarlik, #d9d9d9) !important;
      border-radius: 8px !important; min-height: 34px;
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }
    .input-wrap:hover ::ng-deep .ant-select-selector { border-color: var(--py-kenarlik-hover, #b3b3b3) !important; background: var(--py-arkaplan-hover, #f5f5f5) !important; }
    .input-wrap:focus-within ::ng-deep .ant-select-selector { border-color: var(--py-kenarlik-aktif, #1890ff) !important; background: var(--py-input-arka, #fff) !important; box-shadow: 0 0 0 2px rgba(24,144,255,0.1) !important; }

    .input-wrap ::ng-deep .ant-input-number {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02)) !important;
      border: 1px solid var(--py-kenarlik, #d9d9d9) !important;
      border-radius: 8px !important;
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }
    .input-wrap:hover ::ng-deep .ant-input-number { border-color: var(--py-kenarlik-hover, #b3b3b3) !important; }
    .input-wrap:focus-within ::ng-deep .ant-input-number { border-color: var(--py-kenarlik-aktif, #1890ff) !important; box-shadow: 0 0 0 2px rgba(24,144,255,0.1) !important; }

    .sonuc-secici { display: flex; gap: 8px; }
    .sonuc-btn {
      --_s: #22c55e;
      flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
      padding: 8px 12px; border-radius: 8px; border: 1px solid var(--py-kenarlik, #d9d9d9);
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02));
      font-size: 11px; font-weight: 500; color: var(--py-metin-ikincil, rgba(0,0,0,0.65));
      cursor: pointer; transition: all 0.25s ease; white-space: nowrap;
    }
    .sonuc-btn:hover { border-color: var(--_s); color: var(--_s); }
    .sonuc-btn.secili {
      border-color: var(--_s); color: var(--_s);
      background: color-mix(in srgb, var(--_s) 8%, transparent);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--_s) 12%, transparent);
    }
    .sonuc-dot {
      width: 8px; height: 8px; border-radius: 50%; background: var(--_s);
      box-shadow: 0 0 6px var(--_s);
    }

    @keyframes shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
  `],
})
export class KaliteEditComponent extends FikoYTKokBilesenComponent {
  parametreler = model<any>();
  lookup = inject(HmiLookupService);

  constructor() { super(); this.lookup.personelleriYukle(); }

  p = computed(() => this.parametreler()?.ek || {});

  sonuclar = [
    { deger: 'Uygun', renk: '#22c55e' },
    { deger: 'Uygun Değil', renk: '#ff4d4f' },
    { deger: 'Şartlı Kabul', renk: '#fa8c16' },
  ];

  guncelle(alan: string, deger: any) {
    const params = this.parametreler();
    if (params?.ek) {
      params.ek[alan] = deger;
      this.parametreler.set({ ...params });
    }
  }
}
